/**
 * TenantPersoneTab — le persone di un'azienda, e «Entra come» (23/10/2026).
 *
 * Il super utente entra nell'account di una persona per vedere e fare esattamente ciò che vede e fa lei, con i suoi
 * stessi permessi (sessione assistita di `generateImpersonationToken`, modalità persona). Simone, sul caso di
 * Pierfilippo Fornasiero: «voglio poter vedere gli stessi vantaggi e limiti che ha un utente vero».
 *
 * Prima di aprire la sessione si dice cosa comporta, con le parole che contano: tutto quello che si fa risulta fatto da
 * lei, resta registrato chi è entrato, e la sessione si chiude da sola dopo 30 minuti. Nell'account di un super utente
 * non si entra (il bottone è spento, e il server rifiuta comunque).
 */
import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Paper, Stack, Typography,
} from '@mui/material';
import { Login } from '@mui/icons-material';
import { SoftChip } from './SoftChip';
import { personeDellAzienda, type PersonaDellAzienda } from '../services/personeDellAzienda';
import { startImpersonation } from '../services/impersonationService';
import type { TenantConsoleListItem } from '../services/tenantService';

export const TenantPersoneTab: React.FC<{ tenant: TenantConsoleListItem }> = ({ tenant }) => {
  const [persone, setPersone] = useState<PersonaDellAzienda[] | null>(null);
  const [errore, setErrore] = useState<string | null>(null);
  const [scelta, setScelta] = useState<PersonaDellAzienda | null>(null);
  const [apertura, setApertura] = useState(false);

  useEffect(() => {
    let vivo = true;
    personeDellAzienda(tenant.id)
      .then((p) => vivo && setPersone(p))
      .catch((e) => {
        console.error('[TenantPersoneTab] persone non lette', e);
        if (vivo) setErrore('Non riesco a leggere le persone di questa azienda.');
      });
    return () => { vivo = false; };
  }, [tenant.id]);

  const entra = async () => {
    if (!scelta) return;
    setApertura(true);
    setErrore(null);
    try {
      await startImpersonation(tenant.id, scelta.uid);
      setScelta(null);
    } catch (e) {
      console.error('[TenantPersoneTab] sessione non aperta', e);
      setErrore(e instanceof Error ? e.message : 'Non è stato possibile aprire la sessione.');
    } finally {
      setApertura(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderRadius: 3 }}>
      <Typography variant="h6" sx={{ fontWeight: 700 }}>Persone</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        «Entra come» apre il gestionale nell’account di quella persona, con i suoi stessi permessi: vedi e fai esattamente quello che vede e fa lei.
      </Typography>
      {errore && <Alert severity="error" sx={{ mb: 2 }}>{errore}</Alert>}
      {!persone && !errore && <CircularProgress size={22} />}
      {persone && persone.length === 0 && <Typography variant="body2" color="text.secondary">Nessuna persona in questa azienda.</Typography>}
      <Stack spacing={1}>
        {(persone ?? []).map((p) => (
          <Box
            key={p.uid}
            sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', border: '1px solid', borderColor: 'divider', borderRadius: 2, px: 2, py: 1.25 }}
          >
            <Box sx={{ minWidth: 0, flex: '1 1 240px' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{p.nome}</Typography>
              <Typography variant="caption" color="text.secondary">{p.email ?? '—'}</Typography>
            </Box>
            <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
              {p.ruoli.map((r) => <SoftChip key={r} label={r} tone="default" mono />)}
              {!p.attiva && <SoftChip label="non attiva" tone="warning" />}
              {p.super_utente && <SoftChip label="super utente" tone="error" />}
            </Stack>
            <Button
              variant="outlined"
              startIcon={<Login fontSize="small" />}
              disabled={p.super_utente || !p.attiva}
              onClick={() => setScelta(p)}
              sx={{ minHeight: 40, textTransform: 'none' }}
            >
              Entra come
            </Button>
          </Box>
        ))}
      </Stack>

      <Dialog open={!!scelta} onClose={apertura ? undefined : () => setScelta(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Entri nell’account di {scelta?.nome}</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5}>
            <Typography variant="body2">Vedi e fai esattamente quello che vede e fa {scelta?.nome}, con i suoi permessi, i suoi prodotti e i suoi limiti.</Typography>
            <Typography variant="body2">Tutto quello che fai risulta fatto da {scelta?.nome}: per esempio gli avvisi e i messaggi di Yuma che apri risultano letti, e i preventivi che crei sono a suo nome. I consumi a pagamento (Yuma, fotomontaggi) vanno sulla sua azienda. Resta registrato che sei entrato tu.</Typography>
            <Typography variant="body2">La sessione si chiude da sola dopo 30 minuti, e non butta fuori {scelta?.nome} se sta lavorando.</Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setScelta(null)} disabled={apertura} sx={{ minHeight: 40, textTransform: 'none' }}>Annulla</Button>
          <Button
            variant="contained"
            onClick={() => void entra()}
            disabled={apertura}
            startIcon={apertura ? <CircularProgress size={14} color="inherit" /> : <Login fontSize="small" />}
            sx={{ minHeight: 40, textTransform: 'none', fontWeight: 600 }}
          >
            Entra come {scelta?.nome}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default TenantPersoneTab;
